import { useState, useEffect } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Upload, X, Loader2 } from 'lucide-react'
import type { FoodItem, Category } from '@/shared/types'

interface FoodItemFormProps {
  onCancel: () => void
  onSave: (data: FoodItem, imageFile?: File) => void
  item?: FoodItem
  loading?: boolean
  categories: Category[]
}

export function FoodItemForm({ onCancel, onSave, item, loading = false, categories }: FoodItemFormProps) {
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name))
  const [name, setName] = useState(item?.name || '')
  const [description, setDescription] = useState(item?.description || '')
  const [price, setPrice] = useState(item?.price || 0)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>(typeof item?.imageUrl === 'string' ? item.imageUrl : '')
  const [isAvailable, setIsAvailable] = useState(item?.isAvailable ?? true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [selectedCategories, setSelectedCategories] = useState<string[]>(item?.categories?.map((c) => c.id) || [])

  useEffect(() => {
    setSelectedCategories(item?.categories?.map((c) => c.id) || [])
  }, [item])

  useEffect(() => {
    if (item) {
      setName(item.name || '')
      setDescription(item.description || '')
      setPrice(item.price || 0)
      setImagePreview(typeof item.imageUrl === 'string' ? item.imageUrl : '')
      setIsAvailable(item.isAvailable ?? true)
    }
  }, [item])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (name.trim().length < 4) {
      newErrors.name = 'Name must be at least 4 characters'
    }

    if (description.trim().length < 20) {
      newErrors.description = 'Description must be at least 20 characters'
    }

    if (price <= 0) {
      newErrors.price = 'Price must be greater than 0'
    }

    if (!item && !imageFile) {
      newErrors.image = 'Image is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, image: 'Image size must be less than 5MB' })
        return
      }
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
      setErrors({ ...errors, image: '' })
    }
  }

  const handleSave = () => {
    if (!validate()) return

    const data: FoodItem = {
      id: item?.id || crypto.randomUUID(),
      name: name.trim(),
      description: description.trim(),
      price: Number(price),
      imageUrl: imagePreview,
      isAvailable,
      categories: categories.filter((c) => selectedCategories.includes(c.id)),
    }

    onSave(data, imageFile || undefined)
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview('')
  }

  return (
    <div className="space-y-6 py-4">
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Label htmlFor="name">
            Item Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            placeholder="e.g., Butter Chicken"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setErrors({ ...errors, name: '' })
            }}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
        </div>

        <div className="col-span-2">
          <Label htmlFor="description">
            Description <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="description"
            placeholder="Describe the item in detail (minimum 20 characters)..."
            rows={3}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
              setErrors({ ...errors, description: '' })
            }}
            className={errors.description ? 'border-destructive' : ''}
          />
          {errors.description && <p className="text-sm text-destructive mt-1">{errors.description}</p>}
        </div>

        <div>
          <Label htmlFor="price">
            Price (₹) <span className="text-destructive">*</span>
          </Label>
          <Input
            id="price"
            type="number"
            min="0"
            step="0.01"
            placeholder="299"
            value={price}
            onChange={(e) => {
              setPrice(Number(e.target.value) || 0)
              setErrors({ ...errors, price: '' })
            }}
            className={errors.price ? 'border-destructive' : ''}
          />
          {errors.price && <p className="text-sm text-destructive mt-1">{errors.price}</p>}
        </div>

        <div className="col-span-2">
          <Label>Image {!item && <span className="text-destructive">*</span>}</Label>
          <div className="mt-2">
            {imagePreview ? (
              <div className="relative inline-block">
                <img src={imagePreview} alt="Preview" className="h-48 w-48 object-cover rounded-lg border" />
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute top-2 right-2"
                  onClick={handleRemoveImage}
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-48 h-48 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                <Upload className="w-10 h-10 text-muted-foreground" />
                <span className="mt-2 text-sm text-muted-foreground">Upload Image</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                  disabled={loading}
                />
              </label>
            )}
          </div>
          {errors.image && <p className="text-sm text-destructive mt-1">{errors.image}</p>}
        </div>

        <div className="col-span-2 flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div>
            <p className="font-medium">Available for order</p>
            <p className="text-sm text-muted-foreground">Toggle to mark as available or unavailable</p>
          </div>
          <Switch checked={isAvailable} onCheckedChange={setIsAvailable} disabled={loading} />
        </div>

        <div className="col-span-2 space-y-2">
          <Label>Categories</Label>
          {sortedCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No categories yet. Create categories from your dashboard, then attach them here.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sortedCategories.map((category) => {
                const active = selectedCategories.includes(category.id)
                return (
                  <Button
                    key={category.id}
                    type="button"
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    onClick={() =>
                      setSelectedCategories((prev) =>
                        prev.includes(category.id) ? prev.filter((id) => id !== category.id) : [...prev, category.id]
                      )
                    }
                    disabled={loading}
                    className="rounded-full"
                  >
                    {category.name}
                  </Button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {item ? 'Updating...' : 'Adding...'}
            </>
          ) : (
            `${item ? 'Update' : 'Add'} Item`
          )}
        </Button>
      </div>
    </div>
  )
}
